/**
 * Lazy tool output — the wire shape a chat-read endpoint substitutes for a
 * tool part's `output` when the full payload is too large to ship with the
 * transcript (knowledge-graph expansions, multi-hundred-kB MCP fetches,
 * long `bash` stdout). The client fetches the real output on demand by
 * `toolCallId` and renders the registered tool view with it.
 *
 * The placeholder keeps the output's small top-level scalars (see
 * `isPreservedScalar`) so the renderer's envelope reads keep working without
 * a fetch: `ok` / `error` (soft-failure tier), `innerCapabilityId` /
 * `innerToolName` (capability-tool routing), `isLoading` / `isExecuting`
 * style flags, exit codes. Nested objects, arrays and long strings — the
 * bulk — are dropped and replaced by the `LAZY_TOOL_OUTPUT_KEY` marker.
 *
 * Pure types + guards only: this module is shared by the server shaping code
 * and the client renderer, so it must stay dependency-free.
 */

/** Marker key on a placeholder output. Unlikely to collide with real tool output fields. */
export const LAZY_TOOL_OUTPUT_KEY = "__magnusLazyOutput" as const;

/** Default size (UTF-8 bytes of the serialized output) above which a read path may defer it. */
export const LAZY_TOOL_OUTPUT_THRESHOLD_BYTES = 32 * 1024;

/**
 * Longest string a placeholder keeps verbatim. Long enough for error messages
 * and ids, short enough that no real payload field sneaks through.
 */
export const LAZY_TOOL_OUTPUT_MAX_PRESERVED_STRING = 1024;

export interface LazyToolOutputRef {
  /** UTF-8 bytes of the full serialized output, for "N kB" affordances. */
  bytes: number;
  /** The tool call whose output was deferred — the key the client fetches by. */
  toolCallId: string;
}

export type LazyToolOutput = Record<string, unknown> & {
  [LAZY_TOOL_OUTPUT_KEY]: LazyToolOutputRef;
};

export function isLazyToolOutput(output: unknown): output is LazyToolOutput {
  if (output === null || typeof output !== "object" || Array.isArray(output)) {
    return false;
  }
  const ref = (output as Record<string, unknown>)[LAZY_TOOL_OUTPUT_KEY];
  return (
    ref !== null &&
    typeof ref === "object" &&
    typeof (ref as LazyToolOutputRef).toolCallId === "string" &&
    typeof (ref as LazyToolOutputRef).bytes === "number"
  );
}

/** Whether a top-level output field is small enough to survive in a placeholder. */
export function isPreservedScalar(value: unknown): boolean {
  switch (typeof value) {
    case "boolean":
    case "number":
      return true;
    case "string":
      return value.length <= LAZY_TOOL_OUTPUT_MAX_PRESERVED_STRING;
    default:
      return value === null;
  }
}

/**
 * Build the placeholder for `output`. Callers decide *whether* to defer
 * (size threshold, tool allow-list); this only decides *what survives*.
 */
export function toLazyToolOutput(
  output: Record<string, unknown>,
  ref: LazyToolOutputRef
): LazyToolOutput {
  const placeholder: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(output)) {
    if (isPreservedScalar(value)) {
      placeholder[key] = value;
    }
  }
  placeholder[LAZY_TOOL_OUTPUT_KEY] = ref;
  return placeholder as LazyToolOutput;
}
